import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useMemo, useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { userLearningPathReportPage } from "../constants/reportPages"
import {
  exportUserLearningPathAsCSV,
  getPaginationMeta,
  GetUserLearningPathParams,
  useGetUserLearningPathQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"

type UserLearningPathFilters = Pick<
  GetUserLearningPathParams,
  "created_start" | "created_end" | "updated_start" | "updated_end" | "status"
>

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === 0) return "-"

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`
  }
  return `${remainingMinutes}m`
}

function UserLearningPathPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })

  const [filters, setFilters] = useState({
    created_start: "",
    created_end: "",
    updated_start: "",
    updated_end: "",
    status: "",
  })

  const [appliedFilters, setAppliedFilters] = useState<
    Partial<UserLearningPathFilters>
  >({})

  // Build query parameters
  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } = useGetUserLearningPathQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "userName",
        headerName: "User Name",
        width: 200,
        sortable: false,
        valueGetter: (_value, row) => {
          const firstName = row.user?.first_name || ""
          const lastName = row.user?.last_name || ""
          return `${firstName} ${lastName}`.trim() || "-"
        },
      },
      {
        field: "userEmail",
        headerName: "Email",
        width: 240,
        sortable: false,
        valueGetter: (_value, row) => row.user?.email || "-",
      },
      {
        field: "learningPathTitle",
        headerName: "Learning Path",
        width: 280,
        sortable: false,
        valueGetter: (_value, row) => row.learning_path?.title || "-",
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        sortable: false,
        renderCell: (params) => {
          if (!params.row.status) return "-"

          const statusColor =
            params.row.status === "completed" ? "success" : "info"
          const statusLabel =
            params.row.status === "completed" ? "Completed" : "In Progress"

          return <Chip label={statusLabel} color={statusColor} size="small" />
        },
      },
      {
        field: "time_spent",
        headerName: "Time Spent",
        width: 120,
        sortable: false,
        valueGetter: (_value, row) => formatDuration(row.time_spent),
      },
      {
        field: "created",
        headerName: "Created",
        width: 160,
        sortable: false,
        valueGetter: (_value, row) => {
          return new Date(row.created).toLocaleDateString()
        },
      },
      {
        field: "updated",
        headerName: "Last Updated",
        width: 160,
        sortable: false,
        valueGetter: (_value, row) => {
          return new Date(row.updated).toLocaleDateString()
        },
      },
    ],
    []
  )

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    // Build the filter object, only including non-empty values
    const newFilters: Partial<UserLearningPathFilters> = {}

    if (filters.created_start) {
      newFilters.created_start = new Date(filters.created_start).toISOString()
    }

    if (filters.created_end) {
      newFilters.created_end = new Date(filters.created_end).toISOString()
    }

    if (filters.updated_start) {
      newFilters.updated_start = new Date(filters.updated_start).toISOString()
    }

    if (filters.updated_end) {
      newFilters.updated_end = new Date(filters.updated_end).toISOString()
    }

    if (filters.status) {
      newFilters.status = filters.status as "in_progress" | "completed"
    }

    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(newFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setAppliedFilters({})
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      created_start: "",
      created_end: "",
      updated_start: "",
      updated_end: "",
      status: "",
    })
  }

  const handleExportCSV = () => {
    const exportParams = {
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    }
    exportUserLearningPathAsCSV(exportParams)
  }

  const totalRows = paginationMeta?.total ?? 0

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={userLearningPathReportPage.pageTitle}
        description={userLearningPathReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom={true}>
            Filters
          </Typography>
          <Stack
            spacing={2}
            direction="row"
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Created Date Range"
              startDate={filters.created_start}
              endDate={filters.created_end}
              onStartDateChange={(value) =>
                handleFilterChange("created_start", value)
              }
              onEndDateChange={(value) =>
                handleFilterChange("created_end", value)
              }
            />

            <DateRangeFilter
              label="Updated Date Range"
              startDate={filters.updated_start}
              endDate={filters.updated_end}
              onStartDateChange={(value) =>
                handleFilterChange("updated_start", value)
              }
              onEndDateChange={(value) =>
                handleFilterChange("updated_end", value)
              }
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error">Error loading data. Please try again.</Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          rowCount={totalRows}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          disableColumnFilter
          disableColumnMenu
          sx={{
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            },
          }}
        />
      </Card>
    </Stack>
  )
}

export default UserLearningPathPage
